import { useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import {
  CircleGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DoubleSide,
  IcosahedronGeometry,
  MeshLambertMaterial,
  Quaternion,
  Vector3,
  type Group,
} from 'three';
import type { Building } from '@map/shared';
import { containsPoint, type Territory } from '@map/worldkit';
import { isTouch } from '../lib/useIsMobile.js';
import { buildTerritoryGeometry } from './geometry.js';
import { rng, scatter, useInstanced, type Part } from './Rain.js';

/**
 * Arabic, from arabic.mappedself.com.
 *
 * Vocab is a forest: one tree for every word mastered (looked up while
 * reading, then used in ten accepted sentences across its forms). The tree's
 * species comes from what kind of word it is, so the forest shows the shape
 * of the vocabulary — verbs are date palms and acacias, nouns olives, figs and
 * cedars, adjectives cypresses and pomegranates — and its health from whether
 * the word is still known: a forgotten or long-unreviewed word browns, thins
 * and droops, and greens again once it is answered right. Trees grow for three
 * months after the word is mastered. Each word keeps its species and spot.
 *
 * Comprehension is an oasis: the pool, the green around it and the palms
 * follow recent comprehension, and it dries to mud when nothing has been read.
 *
 * Everything is code-built and instanced, like the rain.
 */
export interface ArabicLandProps {
  territories: Map<string, Territory>;
  buildings: Building[];
  /** A tree was clicked: the forest's region and building, and the word. */
  onOpenWord?: (regionId: string, buildingId: string, wordId: number) => void;
}

type Word = NonNullable<Building['forest']>['words'][number];
export type Species = 'palm' | 'acacia' | 'olive' | 'fig' | 'cedar' | 'cypress' | 'pomegranate';

const SPECIES_BY_KIND: Record<Word['kind'], Species[]> = {
  verb: ['palm', 'acacia'],
  noun: ['olive', 'fig', 'cedar'],
  adjective: ['cypress', 'pomegranate'],
  particle: ['pomegranate', 'cypress'],
  name: ['cedar'],
};

/** A word's species: fixed by its kind and its id. */
export function speciesOf(word: Pick<Word, 'id' | 'kind'>): Species {
  const pool = SPECIES_BY_KIND[word.kind] ?? SPECIES_BY_KIND.particle;
  return pool[Math.floor(rng(`species:${word.id}`)() * pool.length)] as Species;
}

/** Weathering past which a tree is fully wilted (Arabic sends 0.6 for a forgotten word). */
const WILTED = 0.6;
export const wiltOf = (weathering: number) => Math.min(1, Math.max(0, weathering / WILTED));
/** A young tree is two thirds grown; full height ninety days after mastering. */
export function growthOf(masteredAt: string, now = Date.now()): number {
  const t = Date.parse(masteredAt.includes('T') ? masteredAt : `${masteredAt.replace(' ', 'T')}Z`);
  const days = Number.isFinite(t) ? Math.max(0, (now - t) / 86_400_000) : 0;
  return 0.65 + 0.35 * Math.min(1, days / 90);
}

const GROUND = 1.0205;
const SOIL = 0.0203;
/** A full-grown tree's height at full size, in world units. */
const K = 0.016;

const C = {
  trunk: new Color('#6b4a2e'),
  palmTrunk: new Color('#8a6a45'),
  frond: new Color('#5b8f3a'),
  olive: new Color('#8a9a64'),
  fig: new Color('#3f7534'),
  cedar: new Color('#2f5a3f'),
  cypress: new Color('#2b4a2c'),
  acacia: new Color('#7a9440'),
  pomegranate: new Color('#4a7a36'),
  dates: new Color('#b5652a'),
  fruit: new Color('#c8342c'),
  dead: new Color('#9a7b4f'),
  sand: new Color('#d9c28e'),
  green: new Color('#7fa453'),
  mud: new Color('#8a6d4a'),
  water: new Color('#3f86b8'),
  crack: new Color('#6e5534'),
};

export interface Frame {
  territory: Territory;
  up: Vector3;
  east: Vector3;
  north: Vector3;
  reach: number;
}
export function frameOf(territory: Territory): Frame {
  // The middle of the land itself: the layout's site can sit near an edge.
  const up = territory.ring.reduce<Vector3>((sum, v) => sum.add(new Vector3(v.x, v.y, v.z)), new Vector3()).normalize();
  if (!containsPoint(territory.ring, { x: up.x, y: up.y, z: up.z })) up.set(territory.site.x, territory.site.y, territory.site.z).normalize();
  const east = new Vector3().crossVectors(Math.abs(up.y) > 0.9 ? new Vector3(1, 0, 0) : new Vector3(0, 1, 0), up).normalize();
  const north = new Vector3().crossVectors(up, east);
  let reach = 0;
  for (const v of territory.ring) reach = Math.max(reach, up.angleTo(new Vector3(v.x, v.y, v.z)));
  return { territory, up, east, north, reach };
}

type Parts = { trunks: Part[]; crowns: Part[]; cones: Part[]; balls: Part[] };
const Y = new Vector3(0, 1, 0);

/**
 * One tree's parts at `up`, `size` × full height, browned by `wilt` 0..1.
 * Everything is placed along a local frame so it can lean.
 */
export function buildTree(out: Parts, species: Species, up: Vector3, size: number, wilt: number, seed: string) {
  const r = rng(seed);
  const heading = r() * Math.PI * 2;
  const base = new Quaternion().setFromUnitVectors(Y, up).multiply(new Quaternion().setFromAxisAngle(Y, heading));
  const s = K * size;
  // A wilting tree droops: its whole frame tips a little.
  const droop = wilt * 0.35 * (r() < 0.5 ? -1 : 1);
  const lean = new Quaternion().setFromAxisAngle(new Vector3(1, 0, 0), droop + (r() - 0.5) * 0.12);
  const q = base.clone().multiply(lean);
  const along = (h: number, dx = 0, dz = 0) =>
    up
      .clone()
      .multiplyScalar(GROUND)
      .add(new Vector3(dx, h, dz).applyQuaternion(q));
  const tint = (c: Color) => c.clone().lerp(C.dead, wilt * 0.85);
  const shrink = 1 - wilt * 0.35;
  const part = (list: Part[], h: number, sx: number, sy: number, sz: number, colour: Color, dx = 0, dz = 0, extra?: Quaternion) =>
    list.push({ at: along(h, dx, dz), turn: extra ? q.clone().multiply(extra) : q.clone(), scale: new Vector3(sx, sy, sz), colour });
  const fruit = (h: number, spread: number, n: number, colour: Color) => {
    if (wilt > 0.3) return;
    for (let i = 0; i < n; i += 1) {
      const a = r() * Math.PI * 2;
      part(out.balls, h + (r() - 0.5) * spread * 0.6, 0.05 * s, 0.05 * s, 0.05 * s, colour, Math.cos(a) * spread, Math.sin(a) * spread);
    }
  };

  switch (species) {
    case 'palm': {
      // Tall ringed trunk, a crown of long fronds arching out and down, dates beneath.
      const h = 1.05 * s;
      part(out.trunks, h / 2, 0.035 * s, h, 0.035 * s, tint(C.palmTrunk));
      const fronds = 8;
      for (let i = 0; i < fronds; i += 1) {
        const a = (i / fronds) * Math.PI * 2 + r() * 0.3;
        const tilt = new Quaternion()
          .setFromAxisAngle(Y, a)
          .multiply(new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), -(1.3 + wilt * 0.7 + r() * 0.25)));
        const len = 0.42 * s * shrink;
        const out2 = new Vector3(1, 0, 0).applyAxisAngle(Y, a).multiplyScalar(len * 0.42);
        part(out.cones, h - len * 0.08, 0.05 * s, len, 0.012 * s, tint(C.frond), out2.x, out2.z, tilt);
      }
      fruit(h - 0.06 * s, 0.05 * s, 5, C.dates);
      break;
    }
    case 'acacia': {
      // Thin forked trunk under a wide flat umbrella.
      const h = 0.62 * s;
      part(out.trunks, h * 0.35, 0.03 * s, h * 0.7, 0.03 * s, tint(C.trunk));
      part(out.trunks, h * 0.75, 0.02 * s, h * 0.5, 0.02 * s, tint(C.trunk), 0.06 * s, 0, new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), -0.5));
      part(out.trunks, h * 0.75, 0.02 * s, h * 0.5, 0.02 * s, tint(C.trunk), -0.06 * s, 0, new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), 0.5));
      part(out.crowns, h, 0.42 * s * shrink, 0.08 * s * shrink, 0.36 * s * shrink, tint(C.acacia));
      break;
    }
    case 'olive': {
      // Short gnarled trunk, a broad silvery crown in three lobes.
      const h = 0.5 * s;
      part(out.trunks, h * 0.4, 0.05 * s, h * 0.8, 0.05 * s, tint(C.trunk), 0, 0, new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), 0.18));
      for (let i = 0; i < 3; i += 1) {
        const a = (i / 3) * Math.PI * 2 + r();
        const cr = (0.2 + r() * 0.06) * s * shrink;
        part(out.crowns, h + (r() - 0.3) * 0.08 * s, cr, cr * 0.7, cr, tint(C.olive), Math.cos(a) * 0.12 * s, Math.sin(a) * 0.12 * s);
      }
      break;
    }
    case 'fig': {
      // Stout trunk, one big dense round crown.
      const h = 0.55 * s;
      part(out.trunks, h * 0.45, 0.05 * s, h * 0.9, 0.05 * s, tint(C.trunk));
      const cr = 0.3 * s * shrink;
      part(out.crowns, h + cr * 0.4, cr, cr * 0.85, cr, tint(C.fig));
      break;
    }
    case 'cedar': {
      // Straight trunk carrying flat tiers that narrow upward.
      const h = 0.9 * s;
      part(out.trunks, h / 2, 0.04 * s, h, 0.04 * s, tint(C.trunk));
      const tiers = 4;
      for (let i = 0; i < tiers; i += 1) {
        const t = i / (tiers - 1);
        const w = (0.36 - t * 0.24) * s * shrink;
        part(out.crowns, h * (0.35 + t * 0.6), w, 0.05 * s, w * 0.9, tint(C.cedar));
      }
      break;
    }
    case 'cypress': {
      // A tall dark flame.
      const h = 1.0 * s;
      part(out.trunks, 0.08 * s, 0.03 * s, 0.16 * s, 0.03 * s, tint(C.trunk));
      part(out.cones, 0.15 * s + (h * 0.85 * shrink) / 2, 0.11 * s * shrink, h * 0.85 * shrink, 0.11 * s * shrink, tint(C.cypress));
      break;
    }
    case 'pomegranate': {
      // A bushy small tree, red fruit through it.
      const h = 0.38 * s;
      part(out.trunks, h * 0.4, 0.03 * s, h * 0.8, 0.03 * s, tint(C.trunk));
      for (let i = 0; i < 2; i += 1) {
        const cr = (0.18 + r() * 0.05) * s * shrink;
        part(out.crowns, h + i * 0.08 * s, cr, cr * 0.9, cr, tint(C.pomegranate), (r() - 0.5) * 0.1 * s, (r() - 0.5) * 0.1 * s);
      }
      fruit(h + 0.04 * s, 0.14 * s, 6, C.fruit);
      break;
    }
  }
  return { top: along(0.6 * s), height: s };
}

interface Tree {
  word: Word;
  buildingId: string;
  regionId: string;
  up: Vector3;
  height: number;
}

/** Stable spots: each word draws candidates from its own seed, spaced from the trees before it. */
function plant(frame: Frame, words: Word[], seed: string): Array<{ word: Word; up: Vector3; scale: number }> {
  const n = Math.max(1, words.length);
  const spacing = Math.sqrt(frame.territory.area / n);
  // Crowded forests have smaller trees, so crowns never merge into a lawn.
  const scale = Math.min(2.4, (0.55 * spacing) / (0.36 * K));
  const placed: Vector3[] = [];
  const out: Array<{ word: Word; up: Vector3; scale: number }> = [];
  for (const word of words) {
    const r = rng(`${seed}:${word.id}`);
    let best: Vector3 | null = null;
    let bestGap = -1;
    for (const candidate of scatter(frame, 24, r)) {
      const gap = placed.reduce((m, p) => Math.min(m, p.angleTo(candidate)), Infinity);
      if (gap >= spacing * 0.7) {
        best = candidate;
        break;
      }
      if (gap > bestGap) {
        bestGap = gap;
        best = candidate;
      }
    }
    if (!best) continue;
    placed.push(best);
    out.push({ word, up: best, scale });
  }
  return out;
}

export function ArabicLand({ territories, buildings, onOpenWord }: ArabicLandProps) {
  const forest = buildings.find((b) => b.kind === 'forest' && b.forest);
  const reading = buildings.find((b) => b.kind === 'reading' && b.reading);
  const forestTerritory = forest ? territories.get(forest.regionId) : undefined;
  const readingTerritory = reading ? territories.get(reading.regionId) : undefined;

  const { parts, trees } = useMemo(() => {
    const parts: Parts = { trunks: [], crowns: [], cones: [], balls: [] };
    const trees: Tree[] = [];
    if (forest?.forest && forestTerritory) {
      const frame = frameOf(forestTerritory);
      for (const { word, up, scale } of plant(frame, forest.forest.words, forest.id)) {
        const size = scale * growthOf(word.masteredAt) * (0.9 + rng(`size:${word.id}`)() * 0.2);
        const { height } = buildTree(parts, speciesOf(word), up, size, wiltOf(word.weathering), `tree:${word.id}`);
        trees.push({ word, buildingId: forest.id, regionId: forest.regionId, up, height });
      }
    }
    if (reading?.reading && readingTerritory) oasisPalms(parts, frameOf(readingTerritory), reading);
    return { parts, trees };
  }, [forest, forestTerritory, reading, readingTerritory]);

  const material = useMemo(() => new MeshLambertMaterial(), []);
  useEffect(() => () => material.dispose(), [material]);
  const trunks = useInstanced(parts.trunks, () => new CylinderGeometry(1, 1.3, 1, 6), material);
  const crowns = useInstanced(parts.crowns, () => new IcosahedronGeometry(1, 1), material);
  const cones = useInstanced(parts.cones, () => new ConeGeometry(1, 1, 6), material);
  const balls = useInstanced(parts.balls, () => new IcosahedronGeometry(1, 0), material);

  const root = useRef<Group>(null);
  useTreePicking(trees, root, onOpenWord);

  if (!forest && !reading) return null;
  return (
    <group ref={root}>
      {reading?.reading && readingTerritory && <Oasis frame={frameOf(readingTerritory)} building={reading} />}
      <primitive object={trunks} />
      <primitive object={crowns} />
      <primitive object={cones} />
      <primitive object={balls} />
    </group>
  );
}

/** How wet the oasis is: comprehension, less for every day it has gone unread. */
export function wetnessOf(reading: Pick<Building, 'weathering'> & { reading: { score: number } | null }): number {
  return Math.max(0, (reading.reading?.score ?? 0) * (1 - reading.weathering));
}

/** The pool: a fifth of the country across when full, a puddle when dry. */
function poolRadius(area: number, wet: number): number {
  const across = Math.sqrt(area);
  return across * 0.22 * (0.25 + 0.75 * wet);
}

/** Palms ringing the pool, more of them the better you read. */
function oasisPalms(parts: Parts, frame: Frame, building: Building) {
  const wet = wetnessOf(building);
  const n = Math.round(wet * 14);
  const r = rng(`${building.id}:palms`);
  const pool = poolRadius(frame.territory.area, 1);
  for (let i = 0; i < n; i += 1) {
    const a = (i / Math.max(1, n)) * Math.PI * 2 + r() * 0.4;
    const d = pool * (0.95 + r() * 0.5);
    const up = frame.up
      .clone()
      .addScaledVector(frame.east, Math.cos(a) * d)
      .addScaledVector(frame.north, Math.sin(a) * d)
      .normalize();
    if (!containsPoint(frame.territory.ring, { x: up.x, y: up.y, z: up.z })) continue;
    buildTree(parts, 'palm', up, (0.8 + r() * 0.3) * Math.min(2.4, Math.sqrt(frame.territory.area) / (8 * K)), 1 - Math.min(1, wet * 1.4), `${building.id}:palm:${i}`);
  }
}

/** Sand greening toward the water, and a pool that shrinks to mud as it dries. */
function Oasis({ frame, building }: { frame: Frame; building: Building }) {
  const wet = wetnessOf(building);
  const ground = useMemo(() => buildTerritoryGeometry(frame.territory.ring, { elevation: SOIL }), [frame.territory]);
  const area = frame.territory.area;
  const pool = useMemo(() => {
    const radius = poolRadius(area, wet);
    const g = new CircleGeometry(1, 28).rotateX(-Math.PI / 2);
    return { g, radius };
  }, [area, wet]);
  useEffect(
    () => () => {
      ground?.dispose();
      pool.g.dispose();
    },
    [ground, pool],
  );
  const soil = useMemo(() => C.sand.clone().lerp(C.green, wet * 0.8), [wet]);
  const water = useMemo(() => C.mud.clone().lerp(C.water, Math.min(1, wet * 1.6)), [wet]);
  const turn = useMemo(() => new Quaternion().setFromUnitVectors(Y, frame.up), [frame.up]);
  const at = useMemo(() => frame.up.clone().multiplyScalar(1 + SOIL + 0.0004), [frame.up]);
  const material = useMemo(() => new MeshLambertMaterial({ side: DoubleSide }), []);
  useEffect(() => () => material.dispose(), [material]);
  if (!ground) return null;
  return (
    <group>
      <mesh geometry={ground} raycast={() => null}>
        <meshStandardMaterial flatShading color={soil} roughness={0.95} metalness={0} />
      </mesh>
      <mesh geometry={pool.g} position={at} quaternion={turn} scale={[pool.radius, 1, pool.radius * 0.8]} raycast={() => null}>
        <meshLambertMaterial color={water} side={DoubleSide} />
      </mesh>
    </group>
  );
}

const REACH_MIN = 12;
const REACH_MAX = 60;

/** A click (not a drag) near a tree opens its word, judged on screen like the people. */
function useTreePicking(trees: Tree[], root: React.RefObject<Group | null>, onOpen: ArabicLandProps['onOpenWord']) {
  const { camera, gl } = useThree();
  useEffect(() => {
    if (!onOpen || trees.length === 0) return;
    const canvas = gl.domElement;
    const screen = new Vector3();
    const edge = new Vector3();
    const toCamera = new Vector3();
    const world = new Vector3();
    const tops = trees.map((t) => t.up.clone().multiplyScalar(GROUND + t.height * 0.4));
    const pick = (event: PointerEvent, generosity: number): Tree | null => {
      const rect = canvas.getBoundingClientRect();
      const px = event.clientX - rect.left;
      const py = event.clientY - rect.top;
      let best: Tree | null = null;
      let bestDistance = Infinity;
      tops.forEach((local, i) => {
        const tree = trees[i];
        if (!tree) return;
        const position = root.current ? world.copy(local).applyMatrix4(root.current.matrixWorld) : local;
        toCamera.copy(camera.position).sub(position);
        if (toCamera.dot(position) <= 0) return;
        screen.copy(position).project(camera);
        const sx = ((screen.x + 1) / 2) * rect.width;
        const sy = ((1 - screen.y) / 2) * rect.height;
        edge.copy(position).addScaledVector(position, tree.height / position.length()).project(camera);
        const ex = ((edge.x + 1) / 2) * rect.width;
        const ey = ((1 - edge.y) / 2) * rect.height;
        const reach = Math.min(REACH_MAX, Math.max(REACH_MIN, Math.hypot(ex - sx, ey - sy) * 1.2)) * generosity;
        const d = Math.hypot(sx - px, sy - py);
        if (d < reach && d < bestDistance) {
          bestDistance = d;
          best = tree;
        }
      });
      return best;
    };
    let from: { x: number; y: number; id: number } | null = null;
    const onDown = (e: PointerEvent) => (from = { x: e.clientX, y: e.clientY, id: e.pointerId });
    const onUp = (e: PointerEvent) => {
      const began = from;
      from = null;
      if (!began || began.id !== e.pointerId || Math.hypot(e.clientX - began.x, e.clientY - began.y) > 8) return;
      const tree = pick(e, isTouch() ? 2 : 1.3);
      if (tree) onOpen(tree.regionId, tree.buildingId, tree.word.id);
    };
    const onCancel = () => (from = null);
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onCancel);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onCancel);
    };
  }, [trees, root, camera, gl, onOpen]);
}
